import { useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  TextField,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Swal from 'sweetalert2';
import { useForm, Controller } from 'react-hook-form';
import '../Machinegroup/machinegroup.css';
import { saveMasterListSafely } from '../../Services/app/masterservice';

export default function PlantGroupEdit({
  open,
  handleClose,
  handleAdd,
  dialogOpenCount,
  datasource,
  allMachineGroups,
  setDatasource,
  customerId,
  dialogData,
}) {
  const dialogBackgroundColor = dialogOpenCount === 0 ? '#f7f7f7' : '#ededed';
  const componentNameRef = useRef();

  const defaultForm = useMemo(
    () => ({ name: dialogData?.name || '', groupCodes: dialogData?.groupCodes || [] }),
    [dialogData]
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: defaultForm,
    mode: 'onBlur',
  });

  useEffect(() => {
    reset(defaultForm);
  }, [open, dialogData, reset, defaultForm]);

  // machine-group code -> plant name it's already assigned to (excluding the plant being edited)
  const groupToPlant = new Map();
  (datasource || []).forEach((p) => {
    if (p.code === dialogData?.code) return;
    (p.groupCodes || []).forEach((code) => groupToPlant.set(code, p.name || ''));
  });

  const onSubmit = async (data) => {
    try {
      const newName = data.name.trim();

      let savedList = Array.isArray(datasource) ? [...datasource] : [];
      const response = await saveMasterListSafely({
        customerId,
        key: 'plantgroups',
        baselineCount: Array.isArray(datasource) ? datasource.length : 0,
        mutate: (existingPlants) => {
          const isDuplicate = existingPlants.some(
            (item) => item.code !== dialogData?.code && item.name?.trim().toLowerCase() === newName.toLowerCase()
          );
          if (isDuplicate) {
            const e = new Error('duplicate');
            e.code = 'DUP_PLANT';
            throw e;
          }
          const idx = existingPlants.findIndex((item) => item.code === dialogData?.code);
          if (idx === -1) {
            const e = new Error('not found');
            e.code = 'NOT_FOUND';
            throw e;
          }
          const updated = { ...existingPlants[idx], name: newName, groupCodes: data.groupCodes };
          existingPlants[idx] = updated;
          savedList = existingPlants;
          return existingPlants;
        },
      });

      if (response?.msg) {
        Swal.fire(response.msg);
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Plant updated successfully',
          timer: 1500,
          showConfirmButton: false,
        });
      }

      handleClose();
      reset(defaultForm);
      setDatasource(savedList);
      if (handleAdd) handleAdd();
    } catch (error) {
      if (error.code === 'DUP_PLANT') {
        handleClose();
        Swal.fire('Error', 'A plant with this name already exists.', 'error');
        return;
      }
      if (error.code === 'NOT_FOUND') {
        handleClose();
        Swal.fire('Error', 'Plant not found for update.', 'error');
        return;
      }
      if (error.code === 'MASTER_SHRINK_GUARD') {
        Swal.fire('Save blocked to prevent data loss', 'The existing plant list couldn\'t be read correctly, so saving now would have erased it. Nothing was changed — please check your connection and try again.', 'warning');
        return;
      }
      console.error('Error updating plant:', error);
      Swal.fire('Error updating plant: ' + error.message);
      handleClose();
      reset(defaultForm);
    }
  };

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (componentNameRef.current) componentNameRef.current.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      PaperProps={{
        style: { backgroundColor: dialogBackgroundColor, maxWidth: '600px' },
      }}
    >
      <DialogTitle style={{ color: 'black' }}>Edit Plant</DialogTitle>

      <div className="close_modal">
        <Tooltip title="Close">
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{ backgroundColor: '#ffffff' }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>

      <div className="machinedialog">
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'stretch',
              margin: '1rem',
            }}
          >
            <TextField
              inputRef={componentNameRef}
              label="Plant Name"
              type="text"
              fullWidth
              {...register('name', {
                required: 'Plant Name is required',
                maxLength: { value: 100, message: 'Maximum 100 characters' },
              })}
              onBlur={() => trigger('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
              InputLabelProps={{
                required: true,
                sx: { color: 'black', '&.Mui-focused': { color: 'orange' } },
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  height: '3.5rem',
                  '& fieldset': { borderColor: 'black' },
                  '&:hover fieldset': { borderColor: 'black' },
                  '&.Mui-focused fieldset': { borderColor: 'orange' },
                },
                '& .MuiOutlinedInput-input': { color: 'black' },
              }}
            />

            <Controller
              name="groupCodes"
              control={control}
              render={({ field }) => (
                <FormControl
                  size="small"
                  fullWidth
                  error={!!errors.groupCodes}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      height: '3.5rem',
                      alignItems: 'center',
                      '& fieldset': { borderColor: 'black' },
                      '&:hover fieldset': { borderColor: 'black' },
                      '&.Mui-focused fieldset': { borderColor: 'orange' },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'black',
                      left: '14px',
                      top: '50%',
                      transform: 'translateY(-85%) scale(1)',
                      transformOrigin: 'top left',
                      transition: 'all 0.2s ease',
                      pointerEvents: 'none',
                      '&.Mui-focused, &.MuiFormLabel-filled': {
                        color: 'orange',
                        top: '0px',
                        transform: 'translateY(-50%) scale(0.75)',
                      },
                      '&.Mui-error': {
                        color: '#d32f2f',
                      },
                    },
                  }}
                >
                  <InputLabel sx={{ background: '#ededed' }}>Machine Groups</InputLabel>
                  <Select
                    multiple
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.includes('all')) {
                        const selectableGroups = (allMachineGroups || [])
                          .filter((g) => !groupToPlant.has(g.code))
                          .map((g) => g.code);
                        field.onChange(
                          field.value.length === selectableGroups.length ? [] : selectableGroups
                        );
                      } else {
                        field.onChange(value);
                      }
                    }}
                    renderValue={(selected) =>
                      selected
                        .map((code) => (allMachineGroups || []).find((g) => g.code === code)?.name || code)
                        .join(', ')
                    }
                  >
                    <MenuItem value="all">
                      <Checkbox
                        checked={(() => {
                          const selectable = (allMachineGroups || [])
                            .filter((g) => !groupToPlant.has(g.code))
                            .map((g) => g.code);
                          return selectable.length > 0 && field.value.length === selectable.length;
                        })()}
                        sx={{ '&.Mui-checked': { color: '#f47803ff' } }}
                      />
                      <ListItemText primary="All" />
                    </MenuItem>

                    {(allMachineGroups || []).map((group) => {
                      const otherPlant = groupToPlant.get(group.code);
                      const isAllocated = !!otherPlant;
                      const displayName = isAllocated
                        ? `${group.name || group.code} (allocated to ${otherPlant})`
                        : (group.name || group.code);
                      return (
                        <MenuItem
                          key={group.code}
                          value={group.code}
                          disabled={isAllocated}
                          sx={{
                            opacity: isAllocated ? 0.6 : 1,
                            fontStyle: isAllocated ? 'italic' : 'normal',
                          }}
                        >
                          <Checkbox
                            checked={field.value.includes(group.code)}
                            disabled={isAllocated}
                            sx={{ '&.Mui-checked': { color: '#f47803ff' } }}
                          />
                          <ListItemText primary={displayName} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {errors.groupCodes && (
                    <FormHelperText>{errors.groupCodes.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </div>

          <div
            className="form-button text-right"
            align="end"
            style={{ margin: '0.8rem 1rem 1rem 1rem' }}
          >
            <Button
              type="submit"
              variant="contained"
              className="filter_btn btn_orange"
              color="warning"
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
